import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { cn } from '@/lib/utils.ts';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps<T> {
  data: T[];
  columns: any;
  loading?: boolean;
  pageSize?: number;
  className?: string;
  bodyClass?: string;
  currentPage?: number;
  headerClass?: string;
  onRowClick?: (row: T) => void;
  rowsClassName?: string;
  cellClassName?: string;
  isCheckable?: boolean;
  selectedRows?: T[];
  setSelectedRows?: Dispatch<SetStateAction<T[]>>;
}
export const GeneralTable = <T,>({
  data,
  columns,
  loading,
  pageSize,
  className,
  bodyClass,
  onRowClick,
  rowsClassName,
  cellClassName,
  headerClass,
  currentPage,
  isCheckable = false,
  selectedRows,
  setSelectedRows,
}: IProps<T>) => {
  const { t } = useTranslation();
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [isHeaderIndeterminate, setIsHeaderIndeterminate] = useState(false);
  useEffect(() => {
    if (selectedRows?.length === 0) {
      setIsHeaderChecked(false);
      setIsHeaderIndeterminate(false);
    } else if (selectedRows?.length === data.length) {
      setIsHeaderChecked(true);
      setIsHeaderIndeterminate(false);
    } else {
      setIsHeaderChecked(true);
      setIsHeaderIndeterminate(true);
    }
  }, [selectedRows, data]);
  const handleHeaderCheckboxChange = () => {
    if (!setSelectedRows) return;
    if (isHeaderChecked || isHeaderIndeterminate) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...data]);
    }
  };

  const handleRowCheckboxChange = (row: T) => {
    if (!setSelectedRows) return;
    setSelectedRows((prevSelectedRows) => {
      const exists = prevSelectedRows.find(
        (selectedRow) => selectedRow === row,
      );
      return exists
        ? prevSelectedRows.filter((selectedRow) => selectedRow !== row)
        : [...prevSelectedRows, row];
    });
  };

  const defaultSortColumn = columns.find(
    (col: any) => col?.meta?.defaultSort,
  )?.accessorKey;
  const [sorting, setSorting] = useState<SortingState>(
    defaultSortColumn ? [{ id: defaultSortColumn, desc: true }] : [],
  );

  const table = useReactTable({
    data: data || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const BodyContent = () => {
    if (loading) {
      return (
        <TableRow className="w-full">
          <TableCell colSpan={columns.length + 1} className="h-2 !p-0">
            <div className="progress" />
          </TableCell>
        </TableRow>
      );
    }

    const { rows } = table.getRowModel();

    if (!rows.length) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length + 1}
            className="h-24 text-center text-muted-foreground"
          >
            {t('no_results')}
          </TableCell>
        </TableRow>
      );
    }

    return rows?.map((row, index) => (
      <TableRow
        key={index}
        className={cn(
          onRowClick ? 'hover:bg-table-header cursor-pointer' : '',
          rowsClassName,
        )}
        onClick={() => onRowClick && onRowClick(row.original as T)}
      >
        {isCheckable && (
          <TableCell>
            <Checkbox
              checked={selectedRows?.includes(row.original)}
              onCheckedChange={() => handleRowCheckboxChange(row.original)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}

        {currentPage && pageSize && (
          <TableCell className={cn('py-2.5 text-sm', cellClassName)}>
            {(currentPage - 1) * pageSize + index + 1}
          </TableCell>
        )}

        {row.getVisibleCells()?.map((cell, i) => (
          <TableCell
            key={i}
            className={cn('py-2.5 text-sm text-foreground', cellClassName)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };
  const hasAnyFooter = table
    .getAllLeafColumns()
    .some((col) => col.columnDef.footer);

  return (
    <Table className={className}>
      <TableHeader className={cn('bg-card', headerClass)}>
        {table.getHeaderGroups()?.map((headerGroup, index) => (
          <TableRow key={index}>
            {isCheckable && (
              <TableHead className="w-[10px]">
                <Checkbox
                  isMinusIcon
                  checked={isHeaderChecked}
                  onCheckedChange={handleHeaderCheckboxChange}
                  className="border-border bg-card text-sm"
                />
              </TableHead>
            )}
            {currentPage && pageSize && (
              <TableHead className="w-[50px] text-foreground">№</TableHead>
            )}

            {headerGroup.headers?.map((header, i) => (
              <TableHead key={i} className="font-medium text-foreground">
                {!header.isPlaceholder &&
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className={bodyClass}>
        <BodyContent />
      </TableBody>
      {hasAnyFooter && (
        <TableFooter className="bg-card font-medium">
          {table.getFooterGroups().map((footerGroup, index) => (
            <TableRow key={index}>
              {isCheckable && <TableCell />}
              {currentPage && pageSize && <TableCell />}
              {footerGroup.headers.map((header, i) => (
                <TableCell
                  key={i}
                  className="text-nowrap text-xs font-medium text-foreground"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableFooter>
      )}
    </Table>
  );
};
